import { useEffect, useState } from "react";
import { usersAPI } from "@/services/api";
import { User, UserCreateRequest, UserUpdateRequest, UserRole, AdminPasswordChangeRequest } from "@/types/auth";
import { Button } from "@/components/ui/shadcn/button";
import { Input } from "@/components/ui/shadcn/input";
import { Table } from "@/components/ui/shadcn/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/shadcn/dialog";
import { Card } from "@/components/ui/shadcn/card";
import { DialogDescription } from "@radix-ui/react-dialog";
import { Header } from "@/components/layout/Header";
import { useToast } from "@/hooks/use-toast";
import { Pencil, Trash, CheckIcon, UserPlus, Key } from "lucide-react";
import { handleApiError, showSuccessToast } from "@/lib/error-handler";

export default function UsersDashboard() {
  const { toast } = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [isPasswordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [newUser, setNewUser] = useState<UserCreateRequest>({
    name: "",
    email: "",
    password: "",
    phone: "",
    role: "operator",
    is_enabled: true,
  });
  const [passwordChange, setPasswordChange] = useState<AdminPasswordChangeRequest>({
    new_password: "",
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await usersAPI.getUsers(true);
      setUsers(response);

      if (import.meta.env.DEV) {
        console.log("Users fetched successfully:", response.length);
      }
    } catch (error) {
      handleApiError("Fetch Users", error, toast);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.name || !newUser.email || !newUser.password) {
      toast({
        title: "Validation Error",
        description: "Name, Email and Password are required!",
        variant: "error",
      });
      return;
    }

    setLoading(true);
    try {
      const createdUser = await usersAPI.createUser(newUser);
      showSuccessToast(toast, "Success", "User created successfully!");
      setDialogOpen(false);
      fetchUsers();
      resetForm();

      if (import.meta.env.DEV) {
        console.log("User created successfully:", createdUser);
      }
    } catch (error) {
      handleApiError("Create User", error, toast);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    setLoading(true);
    try {
      const updateData: UserUpdateRequest = {
        name: selectedUser.name,
        email: selectedUser.email,
        phone: selectedUser.phone ?? undefined,
        role: selectedUser.role,
        is_enabled: selectedUser.is_enabled,
      };
      const updatedUser = await usersAPI.updateUser(selectedUser.id, updateData);
      showSuccessToast(toast, "Success", "User updated successfully!");
      setDialogOpen(false);
      fetchUsers();
      resetForm();

      if (import.meta.env.DEV) {
        console.log("User updated successfully:", updatedUser);
      }
    } catch (error) {
      handleApiError("Update User", error, toast);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    setLoading(true);
    try {
      await usersAPI.deleteUser(id);
      showSuccessToast(toast, "Success", "User deleted successfully!");
      fetchUsers();

      if (import.meta.env.DEV) {
        console.log("User deleted successfully, ID:", id);
      }
    } catch (error) {
      handleApiError("Delete User", error, toast);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (!selectedUser || !passwordChange.new_password) {
      toast({
        title: "Validation Error",
        description: "Please enter a new password",
        variant: "error",
      });
      return;
    }

    setLoading(true);
    try {
      await usersAPI.changeUserPassword(selectedUser.id, passwordChange);
      showSuccessToast(toast, "Success", "Password changed successfully!");
      setPasswordDialogOpen(false);
      setPasswordChange({ new_password: "" });
    } catch (error) {
      handleApiError("Change Password", error, toast);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setNewUser({
      name: "",
      email: "",
      password: "",
      phone: "",
      role: "operator",
      is_enabled: true,
    });
    setSelectedUser(null);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container py-6">
        <h1 className="text-2xl font-bold text-gray-800">Users Management</h1>
        <Card className="p-6 shadow-lg rounded-xl">
          <div className="flex justify-between mb-4">
            <Button
              className="bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-800 hover:to-blue-900 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 flex items-center"
              onClick={() => {
                resetForm();
                setDialogOpen(true);
              }}
            >
              <UserPlus className="mr-2 h-5 w-5" /> Add User
            </Button>
          </div>
          {loading ? (
            <p className="text-gray-500 text-center">Loading users...</p>
          ) : (
            <Table className="w-full border rounded-md">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2 text-left">Name</th>
                  <th className="p-2 text-left">Email</th>
                  <th className="p-2 text-left">Phone</th>
                  <th className="p-2 text-left">Role</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-t hover:bg-gray-50">
                    <td className="p-2">{user.name}</td>
                    <td className="p-2">{user.email}</td>
                    <td className="p-2">{user.phone || "N/A"}</td>
                    <td className="p-2">{user.role}</td>
                    <td className="p-2">
                      {user.is_deleted ? (
                        <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-800">
                          Deleted
                        </span>
                      ) : !user.is_enabled ? (
                        <span className="px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">
                          Inactive
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs bg-green-100 text-green-800">
                          Active
                        </span>
                      )}
                    </td>
                    <td className="p-2 flex gap-2">
                      <Button
                        variant="outline"
                        className="flex items-center"
                        onClick={() => {
                          setSelectedUser(user);
                          setDialogOpen(true);
                        }}
                        disabled={user.is_deleted}
                      >
                        <Pencil className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        className="flex items-center"
                        onClick={() => {
                          setSelectedUser(user);
                          setPasswordDialogOpen(true);
                        }}
                        disabled={user.is_deleted}
                      >
                        <Key className="h-4 w-4 mr-1" />
                        Change Password
                      </Button>
                      <Button
                        variant="destructive"
                        className="flex items-center"
                        onClick={() => handleDeleteUser(user.id)}
                        disabled={user.is_deleted}
                      >
                        <Trash className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>

        {/* User Dialog (Create & Edit) */}
        {isDialogOpen && (
          <Dialog open={isDialogOpen} onOpenChange={setDialogOpen}>
            <DialogContent className="sm:max-w-[450px] rounded-lg shadow-2xl">
              <DialogHeader>
                <DialogTitle className="text-lg font-bold text-gray-900 dark:text-white">
                  {selectedUser ? "Edit User" : "Create User"}
                </DialogTitle>
                <DialogDescription className="text-sm text-gray-500">
                  {selectedUser
                    ? "Update the user details below."
                    : "Fill in the user details below to create a new user."}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Name</label>
                  <Input
                    className="w-full"
                    placeholder="Enter user's full name"
                    value={selectedUser ? selectedUser.name : newUser.name}
                    onChange={(e) =>
                      selectedUser
                        ? setSelectedUser({
                            ...selectedUser,
                            name: e.target.value,
                          })
                        : setNewUser({ ...newUser, name: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Email</label>
                  <Input
                    className="w-full"
                    placeholder="Enter user's email address"
                    value={selectedUser ? selectedUser.email : newUser.email}
                    onChange={(e) =>
                      selectedUser
                        ? setSelectedUser({
                            ...selectedUser,
                            email: e.target.value,
                          })
                        : setNewUser({ ...newUser, email: e.target.value })
                    }
                  />
                </div>
                {!selectedUser && (
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">Password</label>
                    <Input
                      className="w-full"
                      type="password"
                      placeholder="Enter initial password"
                      value={newUser.password}
                      onChange={(e) =>
                        setNewUser({ ...newUser, password: e.target.value })
                      }
                    />
                  </div>
                )}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Phone</label>
                  <Input
                    className="w-full"
                    placeholder="Enter phone number (optional)"
                    value={
                      selectedUser
                        ? selectedUser.phone ?? ""
                        : newUser.phone ?? ""
                    }
                    onChange={(e) =>
                      selectedUser
                        ? setSelectedUser({
                            ...selectedUser,
                            phone: e.target.value,
                          })
                        : setNewUser({ ...newUser, phone: e.target.value })
                    }
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">Role</label>
                  <select
                    className="w-full p-2 border rounded-md bg-white"
                    value={selectedUser ? selectedUser.role : newUser.role}
                    onChange={(e) =>
                      selectedUser
                        ? setSelectedUser({
                            ...selectedUser,
                            role: e.target.value as UserRole,
                          })
                        : setNewUser({ ...newUser, role: e.target.value as UserRole })
                    }
                  >
                    <option value="operator">Operator</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="flex items-center space-x-2 pt-2">
                  <input
                    type="checkbox"
                    id="is_enabled"
                    checked={selectedUser ? selectedUser.is_enabled : newUser.is_enabled}
                    onChange={(e) =>
                      selectedUser
                        ? setSelectedUser({
                            ...selectedUser,
                            is_enabled: e.target.checked,
                          })
                        : setNewUser({
                            ...newUser,
                            is_enabled: e.target.checked,
                          })
                    }
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label
                    htmlFor="is_enabled"
                    className="text-sm font-medium text-gray-700"
                  >
                    Active
                  </label>
                </div>
                <div className="flex justify-end space-x-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={
                      selectedUser ? handleUpdateUser : handleCreateUser
                    }
                    disabled={loading}
                    className="bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-800 hover:to-blue-900"
                  >
                    <CheckIcon className="h-5 w-5 mr-2" />
                    {loading
                      ? selectedUser
                        ? "Updating..."
                        : "Creating..."
                      : selectedUser
                      ? "Update"
                      : "Create"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}

        {/* Password Change Dialog */}
        {isPasswordDialogOpen && (
          <Dialog open={isPasswordDialogOpen} onOpenChange={setPasswordDialogOpen}>
            <DialogContent className="sm:max-w-[450px] rounded-lg shadow-2xl">
              <DialogHeader>
                <DialogTitle className="text-lg font-bold text-gray-900 dark:text-white">
                  Change Password
                </DialogTitle>
                <DialogDescription className="text-sm text-gray-500">
                  Enter a new password for {selectedUser?.name}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">New Password</label>
                  <Input
                    className="w-full"
                    type="password"
                    placeholder="Enter new password"
                    value={passwordChange.new_password}
                    onChange={(e) =>
                      setPasswordChange({ new_password: e.target.value })
                    }
                  />
                </div>
                <div className="flex justify-end space-x-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setPasswordDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleChangePassword}
                    disabled={loading}
                    className="bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-800 hover:to-blue-900"
                  >
                    <CheckIcon className="h-5 w-5 mr-2" />
                    {loading ? "Changing..." : "Change Password"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </main>
    </div>
  );
}
